import { ActionIcon, Container, Flex, Text, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconEdit, IconPlus, IconTrash } from '@tabler/icons-react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { manufacturersQueryOptions, useDeleteManufacturer } from 'hooks/products'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { MRT_Localization_PL } from 'mantine-react-table/locales/pl/index.cjs'
import { useMemo } from 'react'
import { Manufacturer } from 'types/products'

export default function ManufacturersTable() {
  const { data } = useSuspenseQuery(manufacturersQueryOptions)
  const deleteManufacturer = useDeleteManufacturer()

  function openAddManufacturerModal() {
    modals.openContextModal({
      modal: 'addManufacturer',
      centered: true,
      title: 'Dodaj producenta',
      innerProps: {},
    })
  }

  function openUpdateManufacturerModal(manufacturer: Manufacturer) {
    modals.openContextModal({
      modal: 'updateManufacturer',
      centered: true,
      title: 'Edytuj producenta',
      innerProps: { manufacturer },
    })
  }

  function openDeleteManufacturerModal(manufacturer: Manufacturer) {
    modals.openConfirmModal({
      title: 'Usuń producenta',
      centered: true,
      children: (
        <Text size="sm">
          Czy na pewno chcesz usunąć producenta <b>{manufacturer.name}</b>? Nie będzie można cofnąć tej operacji.
          Wszystkie produkty które mają przypisanego tego producenta zostaną edytowane i zostaną bez producenta.
        </Text>
      ),
      labels: { confirm: 'Usuń producenta', cancel: 'Nie chcę usuwać' },
      confirmProps: { color: 'red' },
      onConfirm: async () => await deleteManufacturer.mutateAsync(manufacturer),
    })
  }

  const columns = useMemo<Array<MRT_ColumnDef<Manufacturer>>>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nazwa',
      },
      {
        accessorKey: 'email',
        header: 'Email',
      },
    ],
    []
  )

  const table = useMantineReactTable({
    columns,
    data,
    layoutMode: 'grid',
    editDisplayMode: 'table',
    enableColumnActions: false,
    enableColumnFilters: false,
    enableTopToolbar: true,
    enableGlobalFilter: false,
    enableHiding: false,
    enableDensityToggle: false,
    enableFilters: false,
    enableFullScreenToggle: false,
    enableBottomToolbar: data.length < 5 ? false : true,
    paginationDisplayMode: 'pages',
    localization: MRT_Localization_PL,
    enableRowActions: true,
    positionActionsColumn: 'last',
    pageCount: 5,
    mantinePaginationProps: {
      rowsPerPageOptions: ['5', '10'],
    },
    initialState: {
      density: 'xs',
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        grow: false,
        size: 100,
      },
    },
    renderTopToolbarCustomActions: () => (
      <Container mr={0} pr={0}>
        <Tooltip label="Dodaj producenta">
          <ActionIcon size="lg" variant="subtle" onClick={openAddManufacturerModal} ml="auto">
            <IconPlus />
          </ActionIcon>
        </Tooltip>
      </Container>
    ),

    renderRowActions: ({ row }) => (
      <Flex gap="sm" justify="end">
        <Tooltip label="Edytuj">
          <ActionIcon
            radius="xl"
            variant="light"
            color="gray"
            onClick={() => openUpdateManufacturerModal(row.original)}
          >
            <IconEdit size={18} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Usuń">
          <ActionIcon radius="xl" variant="light" color="red" onClick={() => openDeleteManufacturerModal(row.original)}>
            <IconTrash size={18} />
          </ActionIcon>
        </Tooltip>
      </Flex>
    ),
  })
  return <MantineReactTable table={table} />
}
